import { useEffect, useMemo, useState } from 'react';
import {
  OPPORTUNITY_STATUSES,
  OPPORTUNITY_STATUS_LABELS,
  type CwOpportunityStatusAliasDto,
  type OpportunityStatus,
} from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwOpportunityStatusAliases, useSetCwOpportunityStatusAliases } from './api.js';

interface AliasRow {
  id: string;
  cwStatusName: string;
  status: OpportunityStatus | '';
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `opp-status-alias-${aliasSeq}`;
}

interface OpportunityStatusAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise opportunity status (a dropdown) onto a PSA opportunity status. On
 * the opportunities sync, a CW opportunity's status sets the synced opportunity's
 * status through these rows; an opportunity whose status has no row keeps its
 * default/previous status (the sync never fails on a missing status alias).
 */
export function OpportunityStatusAliasEditor({
  isOpen,
}: OpportunityStatusAliasEditorProps): JSX.Element {
  const configQuery = useCwOpportunityStatusAliases(isOpen);
  const saveAliases = useSetCwOpportunityStatusAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwStatusName: alias.cwStatusName,
        status: alias.status,
      })),
    );
  }, [config]);

  const discoveredStatuses = config?.discoveredStatuses ?? [];
  const live = config?.live ?? false;

  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwStatusName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredStatuses.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };
  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };
  const addRow = (cwStatusName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwStatusName, status: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwOpportunityStatusAliasDto[] = rows
      .filter((row): row is AliasRow & { status: OpportunityStatus } =>
        Boolean(row.cwStatusName.trim() && row.status),
      )
      .map((row) => ({ cwStatusName: row.cwStatusName.trim(), status: row.status }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-opportunity-statuses';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Opportunity status from ConnectWise status</h3>
        <p className="mt-1 text-sm text-slate-500">
          Map each ConnectWise opportunity status to a PSA opportunity status below. On sync, an
          opportunity&apos;s ConnectWise status sets its PSA status — an opportunity whose status
          has no row here keeps its default/previous status.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading opportunity statuses…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load opportunity statuses.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise opportunity statuses loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise opportunity statuses discovered yet. Configure the connection to load
              suggestions — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredStatuses.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the status aliases. Make sure each row targets a valid opportunity
              status.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No status aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise status</th>
                  <th className="py-2 pr-3 font-medium">PSA opportunity status</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwStatusName}
                        placeholder="e.g. Won"
                        onChange={(e) => updateRow(row.id, { cwStatusName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.status}
                        onChange={(e) =>
                          updateRow(row.id, { status: e.target.value as OpportunityStatus | '' })
                        }
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select status —</option>
                        {OPPORTUNITY_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {OPPORTUNITY_STATUS_LABELS[status]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save statuses
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
